import {
  type ColorInterpolatorFactory,
  InterpolationMethod,
} from "../color-interpolator";
import {
  type HasAverageColor,
  type HasBackgroundColor,
  VirtualPixelMethod,
  type VirtualViewportPixelAccessor,
} from "../pixel-accessor";
import type { ReversePixelMapper } from "../pixel-mapper";
import type { ResampleFilter } from "../resample-filter";
import type {
  ColorResamplerFactory,
  ColorResamplerFactoryOptions,
} from "./ColorResamplerFactory";
import { EwaResampler } from "./EwaResampler";

/**
 * EwaResampler factory class.
 */
export class EwaResamplerFactory implements ColorResamplerFactory {
  /**
   * EwaResamplerFactory constructor.
   *
   * @param colorInterpolatorFactory
   */
  constructor(
    private readonly colorInterpolatorFactory: ColorInterpolatorFactory
  ) {}

  /**
   * @inheritDoc
   */
  readonly supportsEwa = true;

  /**
   * @inheritDoc
   */
  readonly requiresResampleFilter = true;

  /**
   * @inheritDoc
   */
  create(
    image: VirtualViewportPixelAccessor & HasAverageColor & HasBackgroundColor,
    reversePixelMapper: ReversePixelMapper,
    options: ColorResamplerFactoryOptions = {}
  ): EwaResampler {
    const {
      resampleFilter,
      matteColor = [0, 0, 0, 0],
      interpolationMethod = InterpolationMethod.AVERAGE,
      virtualPixelMethod = VirtualPixelMethod.TRANSPARENT,
    } = options;

    if (!resampleFilter) {
      throw new TypeError("EWA resampler requires ResampleFilter.");
    }

    const weightLookupTable = buildWeightLookupTable(resampleFilter);
    const workingSupport = resampleFilter.getWorkingSupport();

    const averageInterpolator = this.colorInterpolatorFactory.create(
      InterpolationMethod.AVERAGE
    );
    const defaultInterpolator =
      interpolationMethod === InterpolationMethod.AVERAGE
        ? averageInterpolator
        : this.colorInterpolatorFactory.create(interpolationMethod);

    return new EwaResampler(
      image,
      reversePixelMapper,
      weightLookupTable,
      workingSupport,
      averageInterpolator,
      defaultInterpolator,
      matteColor,
      virtualPixelMethod
    );
  }
}

/**
 * Weight lookup table array length.
 * @internal
 */
const WEIGHT_LOOKUP_TABLE_WIDTH = 1024;

/**
 * Builds weight lookup table.
 *
 * @internal
 * @param filter Resample filter.
 */
function buildWeightLookupTable(filter: ResampleFilter): number[] {
  const table = [];
  const support = filter.getWorkingSupport();

  const rScale = support * Math.sqrt(1 / WEIGHT_LOOKUP_TABLE_WIDTH);

  for (let Q = 0; Q < WEIGHT_LOOKUP_TABLE_WIDTH; Q++) {
    table[Q] = filter.getWeight(Math.sqrt(Q) * rScale);
  }

  return table;
}
